<script setup lang="ts">
import { onMounted, onUnmounted, computed, reactive, provide } from 'vue'
import { useRoute } from 'vue-router'
import { useUiStore } from './stores/ui'
import { useAuthStore } from './stores/auth'
import Header from './components/Header.vue'
import AppSidebar from './components/AppSidebar.vue'
import MobileBottomNav from './components/MobileBottomNav.vue'
import TableOfContents from './components/TableOfContents.vue'

const route = useRoute()
const ui = useUiStore()
const auth = useAuthStore()

// Page edit actions — wired up by Page.vue, consumed by Header.vue and MobileBottomNav.vue
const pageEditActions = reactive<{ save?: () => Promise<void>; cancel?: () => void; delete?: () => Promise<void>; aiImprove?: () => void; hasChanges?: boolean; canEdit?: boolean }>({})
provide('pageEditActions', pageEditActions)

const shouldShowHeader = computed(() => {
  return !['/login', '/register'].includes(route.path)
})

const isAuthPage = computed(() => {
  return ['/login', '/register'].includes(route.path)
})

const isWikiPage = computed(() => {
  return route.path.startsWith('/wiki/')
})

onMounted(() => {
  ui.init()
  if (!['/login', '/register'].includes(route.path)) {
    auth.syncRole()
  }
})

onUnmounted(() => {
  ui.cleanup()
})
</script>

<template>
  <div class="app-layout">
    <!-- Auth pages: centered card, no chrome -->
    <template v-if="isAuthPage">
      <router-view />
    </template>

    <!-- Main app layout -->
    <template v-else>
      <AppSidebar />

      <div class="main-area" :class="{ 'main-area--sidebar-collapsed': ui.isDesktop && !ui.sidebarOpen }">
        <Header v-if="shouldShowHeader" />

        <div class="content-area">
          <main class="content-main" :class="{ 'content-main--with-toc': isWikiPage && ui.isDesktop }">
            <router-view :key="route.path" />
          </main>

          <!-- Right-side TOC for wiki pages on desktop -->
          <TableOfContents
            v-if="isWikiPage && ui.isDesktop"
            content-selector=".markdown-content"
          />
        </div>
      </div>

      <!-- Mobile bottom nav -->
      <MobileBottomNav v-if="ui.showBottomNav" />
    </template>
  </div>
</template>

<style scoped>
.app-layout {
  display: flex;
  min-height: 100vh;
  background-color: var(--color-bg);
}

.main-area {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  margin-left: 0;
  transition: margin-left var(--transition-base);
}

@media (min-width: 1024px) {
  .main-area {
    margin-left: 0;
  }
}

.content-area {
  display: flex;
  flex: 1;
  min-height: 0;
}

.content-main {
  flex: 1;
  min-width: 0;
  padding: var(--space-lg);
  max-width: var(--content-max-width);
  margin: 0 auto;
  width: 100%;
}

.content-main--with-toc {
  max-width: calc(var(--content-max-width) + var(--toc-width) + var(--space-xl));
  margin: 0 auto 0 0;
  padding-right: var(--space-xl);
}

/* Mobile layout — bottom nav is position:fixed, body scrolls naturally */
@media (max-width: 1023px) {
  .app-layout {
    flex-direction: column;
  }

  .main-area {
    flex: 1;
    min-height: 0;
  }

  .content-area {
    flex: 1;
    min-height: 0;
  }

  .content-main {
    padding-bottom: calc(var(--bottom-nav-height) + var(--space-md) + env(safe-area-inset-bottom, 0px));
  }
}

/* Tablet and mobile padding */
@media (max-width: 640px) {
  .content-main {
    padding: var(--space-md) var(--space-md) calc(var(--bottom-nav-height) + var(--space-md) + env(safe-area-inset-bottom, 0px));
  }
}
</style>
